import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import { InvalidVendorTeamError, teamsApi } from "work/work-agreements/teams/_api/teams-api-service";
import { VendorTeamJSON } from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-schema";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

export type VendorTeamsStoreState = {
  byQuery: {
    entries: Record<string, VendorTeamJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, InvalidVendorTeamError | null>;
  };
  interfaceQueries: {
    entries: Record<string, { workingForMe: boolean }>;
  };
};

const initialState: VendorTeamsStoreState = {
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

const vendorTeamsSlice = createSlice({
  name: "vendorTeams",
  initialState,
  reducers: {
    updateTeamsInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        workingForMe: boolean;
      }>,
    ) => {
      const { interfaceName, workingForMe } = action.payload;
      state.interfaceQueries.entries[interfaceName] = { workingForMe };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(teamsApi.endpoints.getActiveVendorTeams.matchPending, (state, action) => {
      const { interfaceName, workingForMe } = action.meta.arg.originalArgs;
      const key = getTeamsQueryKey(interfaceName, workingForMe);
      state.byQuery.loading[key] = true;
      state.byQuery.error[key] = null;
    });
    builder.addMatcher(teamsApi.endpoints.getActiveVendorTeams.matchFulfilled, (state, action) => {
      const { interfaceName, workingForMe } = action.meta.arg.originalArgs;
      const key = getTeamsQueryKey(interfaceName, workingForMe);
      state.byQuery.entries[key] = action.payload;
      state.byQuery.loading[key] = false;
      state.byQuery.error[key] = null;
    });
    builder.addMatcher(teamsApi.endpoints.getActiveVendorTeams.matchRejected, (state, action) => {
      const { interfaceName, workingForMe } = action.meta.arg.originalArgs;
      const key = getTeamsQueryKey(interfaceName, workingForMe);
      state.byQuery.loading[key] = false;
      state.byQuery.error[key] = new InvalidVendorTeamError("getActiveVendorTeams", action.payload ?? action.error);
    });
  },
});

export const { updateTeamsInterfaceQuery } = vendorTeamsSlice.actions;

const selectTeamsQueryByInterface = createSelector(
  [
    (state: RootState) => state.vendorTeams.interfaceQueries.entries,
    (_: RootState, interfaceName: string) => interfaceName,
  ],
  (entries, interfaceName) => entries[interfaceName],
);
export const getTeamsQueryByInterface = (interfaceName: string) => (state: RootState) =>
  selectTeamsQueryByInterface(state, interfaceName);

const selectTeamsByQuery = createSelector(
  [
    (state: RootState) => state.vendorTeams.byQuery.entries,
    (_: RootState, interfaceName: string, workingForMe: boolean) => getTeamsQueryKey(interfaceName, workingForMe),
  ],
  (entries, key) => {
    const arr = entries[key];
    if (arr === undefined) return;
    return arr.map((json) => VendorTeam.fromJSON(json)).filter((v): v is VendorTeam => !!v);
  },
);
export const getTeamsByQuery = (interfaceName: string, workingForMe: boolean) => (state: RootState) =>
  selectTeamsByQuery(state, interfaceName, workingForMe);

export const getIsLoadingTeamsByQuery = (interfaceName: string, workingForMe: boolean) => (state: RootState) => {
  const key = getTeamsQueryKey(interfaceName, workingForMe);
  return state.vendorTeams.byQuery.loading[key];
};
export const getErrorLoadingTeamsByQuery = (interfaceName: string, workingForMe: boolean) => (state: RootState) => {
  const key = getTeamsQueryKey(interfaceName, workingForMe);
  return state.vendorTeams.byQuery.error[key];
};

function getTeamsQueryKey(interfaceName: string, workingForMe: boolean): string {
  return `${interfaceName}:${workingForMe}`;
}

export default vendorTeamsSlice;
